import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  UserPlus, 
  Mail, 
  Calendar, 
  Clock, 
  Check, 
  X, 
  Copy, 
  Trash2, 
  AlertCircle,
  Shield,
  Users,
  Link
} from 'lucide-react';


const InvitationManager = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    expires_in_days: 7,
    max_registrations: 1
  });
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', content: '' });

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/api/admin/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setInvitations(response.data.invitations);
      }
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      setMessage({ type: 'error', content: 'Failed to load invitations' });
    } finally {
      setLoading(false);
    }
  };

  const sendInvitation = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const token = localStorage.getItem('access_token');
      const response = await api.post(`/api/admin/invite-user`, inviteForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          content: `Invitation sent to ${inviteForm.email}!` 
        });
        
        // Reset form and refresh list
        setInviteForm({ email: '', expires_in_days: 7, max_registrations: 1 });
        setShowInviteForm(false);
        fetchInvitations();
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        content: error.response?.data?.detail || 'Failed to send invitation' 
      });
    } finally {
      setSending(false);
    }
  };

  const revokeInvitation = async (token) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) {
      return;
    }

    try {
      const authToken = localStorage.getItem('access_token');
      await api.delete(`/api/admin/invitations/${token}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      setMessage({ type: 'success', content: 'Invitation revoked successfully' });
      fetchInvitations();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        content: 'Failed to revoke invitation' 
      });
    }
  };

  const copyRegistrationLink = async (invitation) => {
    const registrationUrl = `${window.location.origin}/register?token=${invitation.token}&email=${invitation.email}`;
    
    try {
      await navigator.clipboard.writeText(registrationUrl);
      setMessage({ type: 'success', content: 'Registration link copied to clipboard!' });
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = registrationUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setMessage({ type: 'success', content: 'Registration link copied to clipboard!' });
    }
  };

  const getStatusBadge = (invitation) => {
    if (!invitation.is_active) {
      return <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">Revoked</span>;
    }
    if (invitation.is_expired) {
      return <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">Expired</span>;
    }
    if (invitation.is_fully_used) {
      return <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">Used</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">Active</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Shield className="w-6 h-6 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Account Security</h2>
            <p className="text-gray-600 dark:text-gray-400">Manage user invitations and account access</p>
          </div>
        </div>
        
        <button
          onClick={() => setShowInviteForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          <span>Invite User</span>
        </button>
      </div>

      {/* Message */}
      {message.content && (
        <div className={`p-4 rounded-lg flex items-center space-x-2 ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <Check className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.content}</span>
          <button
            onClick={() => setMessage({ type: '', content: '' })}
            className="ml-auto text-gray-500 hover:text-gray-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Invite New User</h3>
              <button
                onClick={() => setShowInviteForm(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={sendInvitation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expires in (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={inviteForm.expires_in_days}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, expires_in_days: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Maximum Registrations
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={inviteForm.max_registrations}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, max_registrations: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  disabled={sending}
                >
                  {sending ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invitations List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Invitation Tokens ({invitations.length})
            </h3>
          </div>
        </div>

        {invitations.length === 0 ? (
          <div className="p-8 text-center">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No invitations sent yet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Create your first invitation to get started
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {invitations.map((invitation) => (
                  <tr key={invitation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900 dark:text-white">
                          {invitation.email}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invitation)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {invitation.used_count} / {invitation.max_registrations}
                      {invitation.remaining_uses > 0 && (
                        <span className="ml-1 text-green-600">
                          ({invitation.remaining_uses} left)
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(invitation.expires_at)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {invitation.is_active && !invitation.is_expired && !invitation.is_fully_used && (
                          <button
                            onClick={() => copyRegistrationLink(invitation)}
                            className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                            title="Copy registration link"
                          >
                            <Link className="w-4 h-4" />
                          </button>
                        )}
                        
                        {invitation.is_active && (
                          <button
                            onClick={() => revokeInvitation(invitation.token)}
                            className="p-1 text-red-600 hover:text-red-800 transition-colors"
                            title="Revoke invitation"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Security Note */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 dark:text-blue-300">Security Note</p>
            <p className="text-blue-700 dark:text-blue-400 mt-1">
              Only users with valid invitation tokens can create accounts. Invitations expire automatically 
              and can be revoked at any time. Share registration links securely via private channels.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationManager;